/**
 * Particle System setup UI for Shop / Crates Theme Preview.
 * Mirrors Theme Creator particle setup without coupling to admin-theme-creator.
 */
import {
  DEFAULT_PARTICLE_CONFIG,
  THEME_PARTICLE_PRESETS,
  normalizeParticleConfig,
  type ThemeParticleConfig,
  type ThemeParticlePresetId,
  type ThemeScreenTarget,
  type ThemeDecoration,
} from "./theme-types";

export function openParticleSetupModal(initial?: {
  screens?: ThemeScreenTarget[];
  config?: ThemeParticleConfig;
  /** Primary confirm label — e.g. "Save Changes" when editing. */
  confirmLabel?: string;
  /** Optional second action (e.g. "Edit art") that also returns the form values. */
  artLabel?: string;
}): Promise<{
  screens: ThemeScreenTarget[];
  config: ThemeParticleConfig;
  editArt: boolean;
} | null> {
  return new Promise((resolve) => {
    const modal = document.createElement("div");
    modal.className = "themeDrawImportModal";
    const box = document.createElement("div");
    box.className = "modeBox themeDrawImportBox themeParticleSetupBox";
    const h = document.createElement("h4");
    h.textContent = "Particle System";
    box.appendChild(h);

    const body = document.createElement("div");
    body.className = "themeParticleSetupBody";
    box.appendChild(body);

    const locHint = document.createElement("p");
    locHint.className = "accessHint";
    locHint.textContent = "Locations (combinable):";
    body.appendChild(locHint);

    const locKeys: ThemeScreenTarget[] = [
      "background",
      "sidebar",
      "home",
      "settings",
      "shop",
      "challenges",
    ];
    const initialScreens = new Set(initial?.screens || ["background"]);
    const checks = new Map<string, HTMLInputElement>();
    for (const key of locKeys) {
      const lab = document.createElement("label");
      lab.className = "adminThemeCheckLabel";
      const cb = document.createElement("input");
      cb.type = "checkbox";
      cb.checked = initialScreens.has(key);
      checks.set(key, cb);
      lab.appendChild(cb);
      lab.appendChild(
        document.createTextNode(
          ` ${key === "background" ? "Background" : key[0]!.toUpperCase() + key.slice(1)}`,
        ),
      );
      body.appendChild(lab);
    }

    const presetHint = document.createElement("p");
    presetHint.className = "accessHint";
    presetHint.textContent = "Preset (editable below):";
    body.appendChild(presetHint);

    let config: ThemeParticleConfig = normalizeParticleConfig(
      initial?.config || DEFAULT_PARTICLE_CONFIG,
    );
    const presetRow = document.createElement("div");
    presetRow.className = "themeDrawActions";
    for (const id of Object.keys(THEME_PARTICLE_PRESETS) as ThemeParticlePresetId[]) {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "btn";
      b.textContent = THEME_PARTICLE_PRESETS[id].label;
      b.onclick = () => {
        config = { ...THEME_PARTICLE_PRESETS[id].config };
        syncFields();
        for (const btn of presetRow.querySelectorAll("button")) btn.classList.remove("green");
        b.classList.add("green");
      };
      presetRow.appendChild(b);
    }
    body.appendChild(presetRow);

    const fields = document.createElement("div");
    fields.className = "themeParticleFields";
    body.appendChild(fields);

    type Field = {
      key: keyof ThemeParticleConfig;
      label: string;
      min: number;
      max: number;
      step: number;
    };
    const fieldDefs: Field[] = [
      { key: "spawnRate", label: "Spawn Rate (/s)", min: 1, max: 120, step: 1 },
      { key: "fallSpeedMin", label: "Fall Speed min", min: 5, max: 800, step: 1 },
      { key: "fallSpeedMax", label: "Fall Speed max", min: 5, max: 800, step: 1 },
      { key: "driftAmplitude", label: "Drift amplitude", min: 0, max: 120, step: 1 },
      { key: "driftFrequency", label: "Drift frequency", min: 0, max: 2, step: 0.05 },
      { key: "rotationSpeedMin", label: "Rotation min (°/s)", min: -360, max: 360, step: 1 },
      { key: "rotationSpeedMax", label: "Rotation max (°/s)", min: -360, max: 360, step: 1 },
      { key: "scaleMin", label: "Scale min", min: 0.1, max: 3, step: 0.05 },
      { key: "scaleMax", label: "Scale max", min: 0.1, max: 3, step: 0.05 },
      { key: "windAngleDeg", label: "Wind angle (°)", min: -180, max: 180, step: 1 },
      { key: "maxParticles", label: "Max particles", min: 10, max: 300, step: 1 },
      { key: "baseSizePx", label: "Base size (px)", min: 4, max: 64, step: 1 },
    ];
    const inputs = new Map<keyof ThemeParticleConfig, HTMLInputElement>();

    const fadeLab = document.createElement("label");
    fadeLab.className = "adminThemeCheckLabel";
    const fadeCb = document.createElement("input");
    fadeCb.type = "checkbox";
    fadeCb.checked = config.opacityFade;
    fadeCb.onchange = () => {
      config.opacityFade = fadeCb.checked;
    };
    fadeLab.appendChild(fadeCb);
    fadeLab.appendChild(document.createTextNode(" Opacity fade (spawn / exit)"));

    function syncFields() {
      for (const def of fieldDefs) {
        const input = inputs.get(def.key);
        if (input) input.value = String(config[def.key]);
      }
      fadeCb.checked = config.opacityFade;
    }

    for (const def of fieldDefs) {
      const row = document.createElement("label");
      row.className = "themeParticleFieldRow";
      const lab = document.createElement("span");
      lab.textContent = def.label;
      const input = document.createElement("input");
      input.type = "number";
      input.className = "formInput";
      input.min = String(def.min);
      input.max = String(def.max);
      input.step = String(def.step);
      input.value = String(config[def.key]);
      input.oninput = () => {
        const v = Number(input.value);
        if (!Number.isFinite(v)) return;
        switch (def.key) {
          case "spawnRate":
          case "fallSpeedMin":
          case "fallSpeedMax":
          case "driftAmplitude":
          case "driftFrequency":
          case "rotationSpeedMin":
          case "rotationSpeedMax":
          case "scaleMin":
          case "scaleMax":
          case "windAngleDeg":
          case "maxParticles":
          case "baseSizePx":
            config[def.key] = v;
            break;
          default:
            break;
        }
      };
      inputs.set(def.key, input);
      row.appendChild(lab);
      row.appendChild(input);
      fields.appendChild(row);
    }
    body.appendChild(fadeLab);

    function finish(editArt: boolean) {
      const screens = locKeys.filter((k) => checks.get(k)?.checked);
      if (!screens.length) {
        locHint.textContent = "Pick at least one location.";
        return;
      }
      modal.remove();
      resolve({ screens, config: normalizeParticleConfig(config), editArt });
    }

    const row = document.createElement("div");
    row.className = "themeDrawActions themeParticleSetupActions";
    const cancel = document.createElement("button");
    cancel.type = "button";
    cancel.className = "btn";
    cancel.textContent = "Cancel";
    cancel.onclick = () => {
      modal.remove();
      resolve(null);
    };
    row.appendChild(cancel);

    if (initial?.artLabel) {
      const art = document.createElement("button");
      art.type = "button";
      art.className = "btn";
      art.textContent = initial.artLabel;
      art.onclick = () => finish(true);
      row.appendChild(art);
    }

    const ok = document.createElement("button");
    ok.type = "button";
    ok.className = "btn green";
    ok.textContent = initial?.confirmLabel || "Draw particle";
    ok.onclick = () => finish(!!initial?.artLabel ? false : true);
    row.appendChild(ok);
    box.appendChild(row);
    modal.appendChild(box);
    document.body.appendChild(modal);
  });
}


export function particlePlacementFromScreens(screens: ThemeScreenTarget[]): {
  anchor: "viewport" | "sidebar";
  layer: "front" | "back";
} {
  const hasBg = screens.includes("background");
  const onlySidebar = screens.includes("sidebar") && screens.every((s) => s === "sidebar");
  return {
    anchor: onlySidebar ? "sidebar" : "viewport",
    layer: hasBg || !onlySidebar ? "back" : "front",
  };
}


export function newThemeParticleDecorationId(): string {
  return `tp_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export function buildParticleDecoration(opts: {
  imageData: string;
  opacity: number;
  screens: ThemeScreenTarget[];
  config: ThemeParticleConfig;
}): ThemeDecoration {
  const place = particlePlacementFromScreens(opts.screens);
  return {
    id: newThemeParticleDecorationId(),
    kind: "particles",
    imageData: opts.imageData,
    frames: [opts.imageData],
    anchor: place.anchor,
    xPct: 0,
    yPct: 0,
    widthPct: 100,
    heightPct: 100,
    screens: opts.screens,
    zIndex: 0,
    opacity: opts.opacity,
    layer: place.layer,
    particleConfig: opts.config,
  };
}
